import type { DocMigration } from "../docMigration";

const DEFAULT_IDENTIFIER_PREFIX = "identifier_";
const OLD_PARAMETER_ID = "buildingTotalArea";
const NEW_PARAMETER_ID = "buildingConditionedArea";

const TOTAL_AREA_TOKEN_REGEX = parameterIdRegex(OLD_PARAMETER_ID);
const REPLACEMENT_PARAMETER_TOKEN = parameterTokenNew(NEW_PARAMETER_ID);

const TOTAL_AREA_FORMULA_IDENTIFIER = DEFAULT_IDENTIFIER_PREFIX + OLD_PARAMETER_ID;
const CONDITIONED_AREA_FORMULA_IDENTIFIER = DEFAULT_IDENTIFIER_PREFIX + NEW_PARAMETER_ID;

const migration: DocMigration = {
  id: "20260225_replace_building_total_area_with_conditioned_area",
  description:
    "Replace buildingTotalArea with buildingConditionedArea in condition formulas, annotations, ParameterRef objects, and annotation catalog setting templates",
  apply(json: any): any {
    // Migrate conditions on doc entities using BuildingTotalArea
    migrateChildEntityFormulas(json);

    // Migrate annotation text on live plan instances using BuildingTotalArea
    for (const id in json.annotation ?? {}) {
      const annotation = json.annotation[id];
      if (annotation.type === "NOTE" && typeof annotation.text === "string") {
        annotation.text = migrateText(annotation.text);
      }
    }

    for (const id in json.settings ?? {}) {
      const settings = json.settings[id];
      if (settings.type !== "ANNOTATION_CATALOG_SETTINGS") {
        continue;
      }
      for (const templateKey in settings.annotationTemplates ?? {}) {
        const template = settings.annotationTemplates[templateKey];
        for (const componentKey in template.components ?? {}) {
          const component = template.components[componentKey];
          if (component.type === "NOTE" && typeof component.text === "string") {
            component.text = migrateText(component.text);
          }
        }
      }
    }

    // Migrate ParameterRef objects referencing buildingTotalArea anywhere in the doc
    // (viewport title/number/description, QR code url, data table fields, parameterValues, etc.)
    migrateParameterRefs(json);

    return json;
  },
};

export default migration;

function parameterIdRegex(paramId: string): RegExp {
  const regex = new RegExp(`\{\{[ ]*${paramId}[ ]*\}\}`, "g");
  return regex;
}

function parameterTokenNew(paramId: string | undefined) {
  return `{{ ${paramId} }}`;
}

function formulaNew(formula: string) {
  return { type: "FORMULA", formula };
}

function isFormula(v: any): v is { type: "FORMULA"; formula: string } {
  return (
    !!v &&
    typeof v === "object" &&
    v.hasOwnProperty("type") &&
    v.type === "FORMULA" &&
    v.hasOwnProperty("formula") &&
    typeof v.formula === "string"
  );
}

function migrateText(text: string): string {
  return text.replace(TOTAL_AREA_TOKEN_REGEX, REPLACEMENT_PARAMETER_TOKEN);
}

function isParameterRef(v: any): v is { type: "PARAMETER_REF"; id: string } {
  return !!v && typeof v === "object" && v.type === "PARAMETER_REF" && typeof v.id === "string";
}

/**
 * Recursively walks the doc JSON and replaces any ParameterRef with
 * id "buildingTotalArea" to id "buildingConditionedArea".
 */
function migrateParameterRefs(obj: any): void {
  if (!obj || typeof obj !== "object") {
    return;
  }

  if (Array.isArray(obj)) {
    for (const item of obj) {
      migrateParameterRefs(item);
    }
    return;
  }

  for (const key in obj) {
    const value = obj[key];
    if (isParameterRef(value) && value.id === OLD_PARAMETER_ID) {
      value.id = NEW_PARAMETER_ID;
    } else {
      migrateParameterRefs(value);
    }
  }
}

function migrateChildEntityFormulas(doc: any) {
  for (const docEntityName in doc) {
    for (const entityId in doc[docEntityName]) {
      const tableEntity = doc[docEntityName][entityId];
      if (!tableEntity || typeof tableEntity !== "object") {
        continue;
      }

      if (!("disabled" in tableEntity)) {
        continue;
      }

      if (!isFormula(tableEntity.disabled)) {
        continue;
      }

      const updatedFormula = formulaNew(
        tableEntity.disabled.formula.replaceAll(TOTAL_AREA_FORMULA_IDENTIFIER, CONDITIONED_AREA_FORMULA_IDENTIFIER),
      );

      Object.assign(tableEntity, { disabled: updatedFormula });
    }
  }
}
